import { Box, Button, Flex, Input, Text } from "@chakra-ui/react";
import { FileUp, Trash2Icon } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/components/ui/dialog";
import { FormLabel } from "../../ui/components/ui/form";
import useApiRequest from "../hooks/useRequest";

const ImageUpload = ({
  defaultUpload,
  route,
  baseImg,
  onUpload,
  onFileAdded,
  heading,
  size,
  imageLabel,
  subheading,
}: {
  defaultUpload?: boolean;
  route: string;
  baseImg: string;
  onUpload?: () => void;
  onFileAdded?: () => void;
  heading?: string;
  size?: "default" | "small";
  imageLabel?: string;
  subheading?: string;
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [confIsOpen, setConfIsOpen] = useState(false);
  const { apiRequest, apiFileRequest } = useApiRequest();
  const [file, setFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string>(baseImg);
  const [fileSize, setFileSize] = useState<number>(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handleFileDrop(event.dataTransfer.files);
    }
  };

  const uploadFile = useCallback(async () => {
    if (file && fileSize <= 5000000) {
      const response = await apiFileRequest<string>(route, "POST", file, {
        formdataName: "file",
      });

      if (response.status < 300 && response.status >= 200) {
        if (onUpload) {
          onUpload();
        }

        setImageSrc(URL.createObjectURL(file));
        setFile(null);
      }
    }
  }, [file, fileSize, apiFileRequest, route, onUpload]);

  useEffect(() => {
    if (defaultUpload === undefined || defaultUpload === true || file) {
      uploadFile();
    }
  }, [defaultUpload, file, uploadFile]);

  const handleFileDrop = (files: FileList | null) => {
    if (files?.length === 0 || !files) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    const filteredFiles = Array.from(files).filter((file) => allowedTypes.includes(file.type));
    handleUpload(filteredFiles[0]);
  };

  const handleUploadClick = () => {
    const uploadBtn = document.querySelector(`#uploadBtn-${imageLabel}`) as HTMLButtonElement | null;
    if (uploadBtn) {
      uploadBtn.click();
    }
  };

  const handleUpload = (file: File) => {
    setFile(file);
    setFileSize(file.size);
    if (onFileAdded) {
      onFileAdded();
    }
  };

  const handleDeleteClick = async () => {
    setFile(null);
    setConfIsOpen(false);
    if (imageSrc) {
      await apiRequest<string>(route, "DELETE");
      setImageSrc("");
    }
  };

  return (
    <div>
      <Box className={`mt-4 ${size === "small" ? "flex gap-2" : ""}`}>
        {heading && <FormLabel className="w-full font-bold mt-4">{heading} </FormLabel>}
        {subheading && <p className="text-sm">{subheading}</p>}
        <Box
          className={`flex flex-col justify-center ${
            size && size === "small" ? "max-h-80 max-w-80 w-60 h-60" : "min-h-80 my-6"
          } ${isDragging ? "text-slate-50" : "text-slate-400"} ${
            imageSrc || file ? "" : "p-8 bg-slate-200 "
          } gap-6 items-center rounded-lg `}
          backgroundColor={isDragging ? "#DDD" : imageSrc || file ? "" : "#EEE"}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleUploadClick}
        >
          {imageSrc || file ? (
            <>
              {file ? (
                <img
                  src={URL.createObjectURL(file)}
                  alt="Location"
                  className="rounded-lg w-full h-full border border-solid cursor-pointer"
                />
              ) : (
                <img
                  src={imageSrc}
                  alt="Location"
                  className="rounded-lg w-full h-full border border-solid cursor-pointer"
                />
              )}
            </>
          ) : (
            <>
              <FileUp size={size && size === "small" ? 40 : 80} />
              <Text className={`${size && size === "small" ? "text-sm" : "text-lg"} text-center font-medium`}>
                Foto per Drag-and-Drop in dieses Feld ziehen
                <br /> Oder über den Button „Foto hochladen“ auswählen
              </Text>
            </>
          )}
        </Box>
        <Flex className={`gap-4 ${size && size === "small" ? "justify-end flex-col" : "justify-start"}`}>
          <Button
            colorScheme="primary"
            className="flex gap-2 p-2 text-white"
            justifyContent="flex-start"
            variant="outline"
            onClick={handleUploadClick}
          >
            <Box boxSize={5} display="flex" alignItems="center" justifyContent="center">
              <FileUp />
            </Box>
            <span>{imageLabel ?? "Foto"} hochladen</span>
          </Button>
          <Dialog open={confIsOpen} onOpenChange={setConfIsOpen}>
            <DialogTrigger asChild>
              <Button
                borderColor="error"
                textColor="error"
                className="flex gap-2 p-2"
                justifyContent="flex-start"
                variant="outline"
              >
                <Box boxSize={5} display="flex" alignItems="center" justifyContent="center">
                  <Trash2Icon />
                </Box>
                <span>{imageLabel ?? "Foto"} löschen</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>{imageLabel ?? "Foto"} löschen</DialogTitle>
                <DialogDescription>
                  Sind Sie sich sicher, dass sie das {imageLabel ?? "Foto"} unwiderruflich löschen willst?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button
                    colorScheme="primary"
                    className="flex gap-2 p-2 text-white"
                    justifyContent="flex-start"
                    variant="outline"
                  >
                    Close
                  </Button>
                </DialogClose>
                <Button
                  borderColor="error"
                  textColor="error"
                  className="flex gap-2 p-2"
                  justifyContent="flex-start"
                  variant="outline"
                  onClick={handleDeleteClick}
                >
                  Löschen
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog
            open={fileSize > 5000000}
            onOpenChange={() => {
              setFile(null);
              setFileSize(0);
              if (inputRef.current) {
                inputRef.current.value = "";
              }
            }}
          >
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>{imageLabel ?? "Foto"} löschen</DialogTitle>
                <DialogDescription>
                  Die Datei ist zu groß. Bitte wählen Sie eine Datei mit einer maximalen Größe von 5MB.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button
                    colorScheme="primary"
                    className="flex gap-2 p-2 text-white"
                    justifyContent="flex-start"
                    variant="outline"
                  >
                    Close
                  </Button>
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Flex>
        <label id={`uploadBtn-${imageLabel}`}>
          <Input
            className="hidden"
            type="file"
            accept="image/png, image/jpeg"
            ref={inputRef}
            onChange={(e) => {
              e.preventDefault();
              if (e.target?.files && e.target?.files.length > 0) {
                handleUpload(e.target.files[0]);
              }
            }}
          />
        </label>
      </Box>
      <p className="text-sm text-slate-400">
        <span className="text-slate-600">Hinweis:</span> Maximale Dateigröße: 5MB
      </p>
    </div>
  );
};

export default ImageUpload;
